import { useCallback, useEffect, useMemo, useState } from 'react';
import { format, parseISO, differenceInDays, startOfDay } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Plus, Wrench, FileText, ArrowRight, CheckCircle2, Paperclip, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useApp } from '@/contexts/AppContext';
import AppDateField from '@/components/AppDateField';
import type { HomeAdminCategory, HomeAdminStatus, HomeMaintenanceItem, HomeRepairUpdate, HomeMovingStage } from '@/types';
import ActionPackCard from '@/components/ActionPackCard';
import SectionAccordionCard from '@/components/SectionAccordionCard';
import type { TenureType } from './MyHomeTab';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppPanelButton,
  AppEmptyState,
  AppFieldGroup,
  AppInput,
  AppLoadingState,
  AppSectionHeader,
  AppText,
  AppTextArea,
} from '../../../ui-foundation';
import { AppNativeSelect } from '../../../ui-foundation/adapters/web';

const CATEGORY_OPTIONS: { value: HomeAdminCategory; label: string }[] = [
  { value: 'maintenance', label: 'Routine maintenance' },
  { value: 'repair', label: 'Repair issue' },
  { value: 'moving', label: 'Moving task' },
];

const MOVING_STAGE_OPTIONS: { value: HomeMovingStage; label: string }[] = [
  { value: 'before', label: 'Before move' },
  { value: 'move-day', label: 'Move day' },
  { value: 'after', label: 'After move' },
];

const STATUS_CLASSES: Record<HomeAdminStatus, string> = {
  open: 'bg-mustard-light text-accent',
  booked: 'bg-plum-light text-primary',
  done: 'bg-success-light text-success',
};

function formatStatus(status: HomeAdminStatus) {
  if (status === 'booked') return 'Booked';
  if (status === 'done') return 'Done';
  return 'Open';
}

function formatCategory(category: HomeAdminCategory) {
  if (category === 'moving') return 'Moving';
  if (category === 'repair') return 'Repair';
  return 'Maintenance';
}

function formatMovingStage(stage: HomeMovingStage | null | undefined) {
  if (stage === 'move-day') return 'Move day';
  if (stage === 'after') return 'After move';
  return 'Before move';
}

function formatFileSize(bytes: number | null | undefined) {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function notifySectionRefresh() {
  window.dispatchEvent(new CustomEvent('adulting:data-changed'));
}

type HomeAdminAccordionSection = 'support' | 'moving' | 'maintenance' | 'routes';

export default function HomeAdminTab() {
  const navigate = useNavigate();
  const { user } = useApp();
  const [items, setItems] = useState<HomeMaintenanceItem[]>([]);
  const [updates, setUpdates] = useState<HomeRepairUpdate[]>([]);
  const [tenure, setTenure] = useState<TenureType | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [savingItem, setSavingItem] = useState(false);
  const [savingEvidenceId, setSavingEvidenceId] = useState<string | null>(null);
  const [evidenceOpenFor, setEvidenceOpenFor] = useState<string | null>(null);
  const [openSection, setOpenSection] = useState<HomeAdminAccordionSection | null>(null);

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<HomeAdminCategory>('maintenance');
  const [dueDate, setDueDate] = useState('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [movingStage, setMovingStage] = useState<HomeMovingStage>('before');

  const [evidenceNote, setEvidenceNote] = useState('');
  const [evidenceContact, setEvidenceContact] = useState('');
  const [evidenceCost, setEvidenceCost] = useState('');
  const [evidenceFile, setEvidenceFile] = useState<globalThis.File | null>(null);

  const fetchAll = useCallback(async () => {
    if (!user) return;
    const [itemsResult, updatesResult, profileResult] = await Promise.all([
      supabase
        .from('home_maintenance_items' as any)
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false }),
      supabase
        .from('home_repair_updates' as any)
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false }),
      supabase
        .from('home_profiles')
        .select('tenure')
        .eq('user_id', user.id)
        .maybeSingle(),
    ]);

    setItems(((itemsResult.data || []) as any[]).map((item) => ({
      id: item.id,
      title: item.title,
      category: item.category,
      status: item.status,
      dueDate: item.due_date,
      location: item.location || '',
      notes: item.notes || '',
      movingStage: item.moving_stage || null,
    })));
    setUpdates(((updatesResult.data || []) as any[]).map((item) => ({
      id: item.id,
      maintenanceItemId: item.maintenance_item_id,
      note: item.note,
      contactName: item.contact_name || '',
      cost: item.cost == null ? null : Number(item.cost),
      updateDate: item.update_date,
      createdAt: item.created_at,
      attachmentFileName: item.attachment_file_name || null,
      attachmentFileType: item.attachment_file_type || null,
      attachmentFileSize: item.attachment_file_size == null ? null : Number(item.attachment_file_size),
      attachmentStoragePath: item.attachment_storage_path || null,
    })));
    setTenure(((profileResult.data as { tenure: TenureType | null } | null)?.tenure) || null);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const resetForm = () => {
    setTitle('');
    setCategory('maintenance');
    setDueDate('');
    setLocation('');
    setNotes('');
    setMovingStage('before');
  };

  const saveItem = async () => {
    if (!user || !title.trim()) return;
    setSavingItem(true);
    await supabase.from('home_maintenance_items' as any).insert({
      user_id: user.id,
      title: title.trim(),
      category,
      due_date: dueDate || null,
      location: location.trim() || null,
      notes: notes.trim() || null,
      moving_stage: category === 'moving' ? movingStage : null,
    });
    setSavingItem(false);
    setShowForm(false);
    resetForm();
    notifySectionRefresh();
    void fetchAll();
  };

  const saveStarterItems = async (starterItems: Array<{
    title: string;
    category: HomeAdminCategory;
    due_date?: string | null;
    location?: string | null;
    notes?: string | null;
    moving_stage?: HomeMovingStage | null;
  }>) => {
    if (!user) return;
    await supabase.from('home_maintenance_items' as any).insert(
      starterItems.map((item) => ({
        user_id: user.id,
        title: item.title,
        category: item.category,
        due_date: item.due_date || null,
        location: item.location || null,
        notes: item.notes || null,
        moving_stage: item.category === 'moving' ? item.moving_stage || 'before' : null,
      })),
    );
    toast.success(`${starterItems.length} home task${starterItems.length === 1 ? '' : 's'} added.`);
    notifySectionRefresh();
    void fetchAll();
  };

  const updateStatus = async (itemId: string, status: HomeAdminStatus) => {
    await supabase.from('home_maintenance_items' as any).update({ status }).eq('id', itemId);
    notifySectionRefresh();
    void fetchAll();
  };

  const saveEvidence = async (itemId: string) => {
    if (!user || (!evidenceNote.trim() && !evidenceFile)) return;
    setSavingEvidenceId(itemId);
    let attachmentStoragePath: string | null = null;

    try {
      if (evidenceFile) {
        attachmentStoragePath = `${user.id}/home-evidence/${itemId}/${Date.now()}_${evidenceFile.name}`;
        const { error: uploadError } = await supabase.storage.from('user-documents').upload(attachmentStoragePath, evidenceFile);
        if (uploadError) throw uploadError;
      }

      const note = evidenceNote.trim() || (evidenceFile ? `Added evidence attachment: ${evidenceFile.name}` : '');
      const { error: insertError } = await supabase.from('home_repair_updates' as any).insert({
        user_id: user.id,
        maintenance_item_id: itemId,
        note,
        contact_name: evidenceContact.trim() || null,
        cost: evidenceCost ? Number(evidenceCost) : null,
        attachment_file_name: evidenceFile?.name || null,
        attachment_file_type: evidenceFile?.type || null,
        attachment_file_size: evidenceFile?.size || null,
        attachment_storage_path: attachmentStoragePath,
      });
      if (insertError) {
        if (attachmentStoragePath) {
          await supabase.storage.from('user-documents').remove([attachmentStoragePath]).catch(() => undefined);
        }
        throw insertError;
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save the evidence update.');
      setSavingEvidenceId(null);
      return;
    }

    setSavingEvidenceId(null);
    setEvidenceOpenFor(null);
    setEvidenceNote('');
    setEvidenceContact('');
    setEvidenceCost('');
    setEvidenceFile(null);
    notifySectionRefresh();
    void fetchAll();
  };

  const pickEvidenceFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf,.doc,.docx,.txt,.heic,.heif';
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Maximum file size is 50MB.');
        return;
      }
      setEvidenceFile(file);
    };
    input.click();
  };

  const openAttachment = async (update: HomeRepairUpdate) => {
    if (!update.attachmentStoragePath) return;
    const { data, error } = await supabase.storage.from('user-documents').createSignedUrl(update.attachmentStoragePath, 60);
    if (error || !data?.signedUrl) {
      toast.error('Could not open that attachment.');
      return;
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
  };

  const stats = useMemo(() => {
    const openItems = items.filter((item) => item.status !== 'done');
    const dueSoon = openItems.filter((item) => {
      if (!item.dueDate) return false;
      const days = differenceInDays(parseISO(item.dueDate), startOfDay(new Date()));
      return days <= 14;
    });

    return {
      openItems: openItems.length,
      dueSoon: dueSoon.length,
      evidenceEntries: updates.length,
    };
  }, [items, updates]);

  const movingChecklist = useMemo(() => {
    const movingItems = items.filter((item) => item.category === 'moving');
    return MOVING_STAGE_OPTIONS.map((stage) => {
      const stageItems = movingItems.filter((item) => (item.movingStage || 'before') === stage.value);
      const openItems = stageItems.filter((item) => item.status !== 'done');
      return {
        ...stage,
        total: stageItems.length,
        open: openItems.length,
        nextItem: openItems[0] || null,
      };
    });
  }, [items]);

  const copyRepairMessage = async () => {
    const message = tenure === 'renting-private' || tenure === 'renting-social'
      ? `Hi,\n\nI’m reporting a repair issue at my home and want to keep this in writing.\n\nIssue:\nLocation:\nWhen it started:\nImpact:\n\nPlease confirm what happens next and when someone will attend.\n\nThanks`
      : `Hi,\n\nI need to get this repair booked in and want to keep the details in one written thread.\n\nIssue:\nLocation:\nWhen it started:\nWhat I need help with next:\n\nPlease confirm the next step and likely timing.\n\nThanks`;
    await navigator.clipboard.writeText(message);
    toast.success('Repair message copied.');
  };

  const addRepairStarter = async () => {
    await saveStarterItems([
      {
        title: 'Log repair issue and first written contact',
        category: 'repair',
        notes: 'Capture what happened, when it started, and who needs to respond.',
      },
    ]);
  };

  const toggleSection = (section: HomeAdminAccordionSection) => {
    setOpenSection((current) => (current === section ? null : section));
  };

  const addMovingChecklist = async () => {
    await saveStarterItems([
      {
        title: 'Confirm notice, contract, or completion dates',
        category: 'moving',
        moving_stage: 'before',
        notes: 'Keep the dates and who is waiting on what in one place.',
      },
      {
        title: 'Book removals, childcare cover, or packing help',
        category: 'moving',
        moving_stage: 'before',
        notes: 'Use this for the practical help that has to be lined up before the move.',
      },
      {
        title: 'Take meter readings and photo evidence',
        category: 'moving',
        moving_stage: 'move-day',
        notes: 'Use this to track handover proof, inventory photos, and final readings.',
      },
      {
        title: 'Keep key handover and condition proof together',
        category: 'moving',
        moving_stage: 'move-day',
        notes: 'Record who handed over keys, what was agreed, and any visible condition issues.',
      },
      {
        title: 'Update council tax, utilities, and address changes',
        category: 'moving',
        moving_stage: 'after',
        notes: 'List each service or record that needs updating after the move.',
      },
      {
        title: 'Update GP, school, bank, and employer records',
        category: 'moving',
        moving_stage: 'after',
        notes: 'Use one checklist so address changes do not get missed across the places that matter.',
      },
    ]);
  };

  if (loading) {
    return (
      <AppLoadingState
        title="Loading home admin"
        body="Pulling your repairs, moving jobs, and evidence trail into one place."
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-3">
        <MetricCard label="Open" value={stats.openItems} />
        <MetricCard label="Due soon" value={stats.dueSoon} />
        <MetricCard label="Evidence" value={stats.evidenceEntries} />
      </div>

      <SectionAccordionCard
        emoji="🧰"
        title="Home admin support"
        subtitle="Keep repair support tucked away until you need it. Moving house stays in the Moving checklist and helper-pack flow."
        open={openSection === 'support'}
        onToggle={() => toggleSection('support')}
      >
        <div className="grid gap-4">
          <ActionPackCard
            icon="🧰"
            eyebrow="Repair Pack"
            title={tenure === 'renting-private' || tenure === 'renting-social' ? 'Keep landlord and repair issues in one written trail' : 'Keep repair jobs and contractor follow-up in one place'}
            description={tenure === 'renting-private' || tenure === 'renting-social'
              ? 'Use one flow for the issue, the written contact, and every follow-up so repairs are easier to chase if they drag.'
              : 'Log the job, book the work, and keep every cost or update in one clean trail instead of scattered messages.'}
            steps={[
              'Record the issue with location, date, and what is affected.',
              'Send one clear written message instead of re-explaining the problem each time.',
              'Add evidence updates as replies, visits, or costs happen.',
            ]}
            actions={[
              { label: 'Add repair starter', onClick: () => void addRepairStarter(), primary: true },
              { label: tenure === 'renting-private' || tenure === 'renting-social' ? 'Copy landlord message' : 'Copy contractor message', onClick: () => void copyRepairMessage() },
            ]}
          />
        </div>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="📦"
        title="Moving checklist"
        subtitle="Keep move-day jobs and follow-on admin grouped in one calmer flow."
        open={openSection === 'moving'}
        onToggle={() => toggleSection('moving')}
      >
        <div className="space-y-4">
          {movingChecklist.some((stage) => stage.total > 0) ? (
            <AppCard>
              <AppSectionHeader
                title="Moving checklist"
                subtitle="Keep the move in staged chunks so the pre-move, move-day, and after-move jobs stop blending into one stressful list."
                action={(
                  <AppButton size="sm" tone="outline" onPress={() => void addMovingChecklist()}>
                    Add staged checklist
                  </AppButton>
                )}
              />
              <div className="grid gap-3 md:grid-cols-3">
                {movingChecklist.map((stage) => (
                  <AppCard key={stage.value} backgroundColor="$background" px="$sm" py="$sm">
                    <div className="flex items-center justify-between gap-3">
                      <AppText fontSize="$sm" fontWeight="700">{stage.label}</AppText>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                        {stage.open}/{stage.total || 0} open
                      </span>
                    </div>
                    <AppText className="mt-2" fontSize="$xs" color="$colorMuted">
                      {stage.nextItem ? `Next: ${stage.nextItem.title}` : 'No tasks in this stage yet.'}
                    </AppText>
                  </AppCard>
                ))}
              </div>
            </AppCard>
          ) : null}

          <AppCard>
            <AppSectionHeader
              title="Moving house flow"
              subtitle="Use the same home admin flow to keep practical move jobs and the follow-on admin together."
            />
            <div className="space-y-2">
              {[
                'Log the jobs that must happen before the move',
                'Keep document storage ready for tenancy, mortgage, or utility paperwork',
                'Update bills once the new place is live',
              ].map((step) => (
                <AppCard key={step} backgroundColor="$background" px="$sm" py="$sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={14} className="shrink-0 text-primary" />
                    <AppText fontSize="$sm">{step}</AppText>
                  </div>
                </AppCard>
              ))}
            </div>
            <AppButtonRow mt="$md">
              <AppButton size="sm" onPress={() => navigate('/documents')}>
                Open documents
              </AppButton>
              <AppButton size="sm" tone="outline" onPress={() => navigate('/home?tab=my-home&section=bills')}>
                Review bills
              </AppButton>
              <AppButton size="sm" tone="outline" onPress={() => navigate('/guides?section=home')}>
                Open support hub
              </AppButton>
            </AppButtonRow>
          </AppCard>
        </div>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="🛠️"
        title="Repairs & maintenance"
        subtitle="Keep the issue, next action, and evidence trail together."
        open={openSection === 'maintenance'}
        onToggle={() => toggleSection('maintenance')}
      >
        <AppCard>
          <AppSectionHeader
            title="Repairs & maintenance"
            subtitle="Keep the issue, next action, and evidence trail in one place instead of spread across notes and email."
            action={(
              <AppButton size="sm" onPress={() => setShowForm((current) => !current)}>
                <Plus size={12} />
                {showForm ? 'Cancel' : 'Add'}
              </AppButton>
            )}
          />

          {showForm && (
            <AppCard backgroundColor="$mutedBackground" className="animate-fade-in">
              <AppFieldGroup label="Task title">
                <AppInput
                  tag="input"
                  value={title}
                  onChange={(event) => setTitle((event.target as HTMLInputElement).value)}
                  placeholder="Boiler service, leak in kitchen, notify landlord…"
                />
              </AppFieldGroup>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <AppFieldGroup label="Category">
                  <AppNativeSelect
                    value={category}
                    onChange={(event) => setCategory(event.target.value as HomeAdminCategory)}
                  >
                    {CATEGORY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </AppNativeSelect>
                </AppFieldGroup>
                {category === 'moving' ? (
                  <AppFieldGroup label="Moving stage">
                    <AppNativeSelect
                      value={movingStage}
                      onChange={(event) => setMovingStage(event.target.value as HomeMovingStage)}
                    >
                      {MOVING_STAGE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </AppNativeSelect>
                  </AppFieldGroup>
                ) : (
                  <AppDateField label="Due date" value={dueDate} onChange={setDueDate} />
                )}
              </div>
              {category === 'moving' && (
                <AppDateField label="Target date" value={dueDate} onChange={setDueDate} />
              )}
              <AppFieldGroup label="Location">
                <AppInput
                  tag="input"
                  value={location}
                  onChange={(event) => setLocation((event.target as HTMLInputElement).value)}
                  placeholder="Kitchen, hallway, bathroom…"
                />
              </AppFieldGroup>
              <AppFieldGroup label="Notes">
                <AppTextArea
                  value={notes}
                  onChange={(event) => setNotes((event.target as HTMLTextAreaElement).value)}
                  placeholder="What happened? Who have you contacted? What needs doing next?"
                  rows={3}
                />
              </AppFieldGroup>
              <AppButton onPress={saveItem} disabled={!title.trim() || savingItem}>
                {savingItem ? 'Saving…' : 'Save item'}
              </AppButton>
            </AppCard>
          )}

          {items.length === 0 ? (
            <AppEmptyState
              title="No home admin items yet"
              body="Add repairs, maintenance, or moving tasks here so the next action stays visible."
            />
          ) : (
            <div className="space-y-3">
              {items.map((item) => {
                const itemUpdates = updates.filter((update) => update.maintenanceItemId === item.id);
                const dueLabel = item.dueDate ? format(parseISO(item.dueDate), 'd MMM yyyy') : 'No due date yet';

                return (
                  <AppCard key={item.id} backgroundColor="$background" px="$sm" py="$sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <AppText fontSize="$sm" fontWeight="700">{item.title}</AppText>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_CLASSES[item.status]}`}>
                            {formatStatus(item.status)}
                          </span>
                          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                            {formatCategory(item.category)}
                          </span>
                          {item.category === 'moving' && (
                            <span className="rounded-full bg-[#F0F4F6] px-2 py-0.5 text-[10px] font-medium text-foreground">
                              {formatMovingStage(item.movingStage)}
                            </span>
                          )}
                        </div>
                        <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                          {dueLabel}
                          {item.location ? ` · ${item.location}` : ''}
                        </AppText>
                        {item.notes && (
                          <AppText className="mt-2" fontSize="$sm" color="$colorMuted">{item.notes}</AppText>
                        )}
                      </div>
                      <Wrench size={16} className="shrink-0 text-muted-foreground" />
                    </div>

                    <AppButtonRow mt="$sm">
                      {item.status !== 'booked' && item.status !== 'done' && (
                        <AppButton size="sm" tone="outline" onPress={() => updateStatus(item.id, 'booked')}>
                          Mark booked
                        </AppButton>
                      )}
                      {item.status !== 'done' && (
                        <AppButton size="sm" tone="outline" onPress={() => updateStatus(item.id, 'done')}>
                          Mark done
                        </AppButton>
                      )}
                      {item.status === 'done' && (
                        <AppButton size="sm" tone="outline" onPress={() => updateStatus(item.id, 'open')}>
                          Reopen
                        </AppButton>
                      )}
                      <AppButton
                        size="sm"
                        tone="outline"
                        onPress={() => {
                          setEvidenceOpenFor((current) => current === item.id ? null : item.id);
                          setEvidenceNote('');
                          setEvidenceContact('');
                          setEvidenceCost('');
                          setEvidenceFile(null);
                        }}
                      >
                        Add evidence
                      </AppButton>
                    </AppButtonRow>

                    {evidenceOpenFor === item.id && (
                      <AppCard backgroundColor="$mutedBackground" className="mt-3 animate-fade-in">
                        <AppFieldGroup label="Evidence note">
                          <AppTextArea
                            value={evidenceNote}
                            onChange={(event) => setEvidenceNote((event.target as HTMLTextAreaElement).value)}
                            placeholder="Spoke to landlord, uploaded photo, chased contractor…"
                            rows={3}
                          />
                        </AppFieldGroup>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          <AppFieldGroup label="Contact name">
                            <AppInput
                              tag="input"
                              value={evidenceContact}
                              onChange={(event) => setEvidenceContact((event.target as HTMLInputElement).value)}
                              placeholder="Contact name"
                            />
                          </AppFieldGroup>
                          <AppFieldGroup label="Cost">
                            <AppInput
                              tag="input"
                              type="number"
                              value={evidenceCost}
                              onChange={(event) => setEvidenceCost((event.target as HTMLInputElement).value)}
                              placeholder="Cost"
                            />
                          </AppFieldGroup>
                        </div>
                        <AppCard backgroundColor="$background" borderStyle="dashed" px="$sm" py="$sm">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="min-w-0">
                              <AppText fontSize="$sm" fontWeight="700">Photo or file attachment (optional)</AppText>
                              <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                                Keep the actual photo, quote, invoice, or written proof with the evidence update.
                              </AppText>
                            </div>
                            <AppButton size="sm" tone="outline" onPress={pickEvidenceFile}>
                              <Paperclip size={12} className="inline mr-1" />
                              {evidenceFile ? 'Replace file' : 'Attach file'}
                            </AppButton>
                          </div>
                          {evidenceFile && (
                            <AppCard backgroundColor="$cardBackground" className="mt-3" px="$sm" py="$sm">
                              <div className="min-w-0">
                                <AppText className="truncate" fontSize="$sm" fontWeight="700">{evidenceFile.name}</AppText>
                                <AppText fontSize="$xs" color="$colorMuted">
                                  {formatFileSize(evidenceFile.size) || 'Attachment ready'}
                                </AppText>
                              </div>
                              <AppButton size="sm" tone="outline" onPress={() => setEvidenceFile(null)}>
                                Remove
                              </AppButton>
                            </AppCard>
                          )}
                        </AppCard>
                        <AppButton
                          onPress={() => saveEvidence(item.id)}
                          disabled={(!evidenceNote.trim() && !evidenceFile) || savingEvidenceId === item.id}
                        >
                          {savingEvidenceId === item.id ? 'Saving…' : 'Save evidence update'}
                        </AppButton>
                      </AppCard>
                    )}

                    {itemUpdates.length > 0 && (
                      <AppCard className="mt-3" px="$sm" py="$sm">
                        <AppText fontSize="$xs" fontWeight="700" textTransform="uppercase" letterSpacing={0.8} color="$colorMuted">
                          Evidence trail
                        </AppText>
                        <div className="space-y-2">
                          {itemUpdates.slice(0, 3).map((update) => (
                            <AppCard key={update.id} backgroundColor="$background" px="$sm" py="$sm">
                              <div className="flex items-center justify-between gap-3">
                                <AppText fontSize="$xs" fontWeight="700">
                                  {format(parseISO(update.updateDate), 'd MMM yyyy')}
                                </AppText>
                                {update.cost != null && (
                                  <AppText fontSize="$xs" color="$colorMuted">£{update.cost.toFixed(2)}</AppText>
                                )}
                              </div>
                              <AppText className="mt-1" fontSize="$sm" color="$colorMuted">{update.note}</AppText>
                              {update.contactName && (
                                <AppText className="mt-1" fontSize="$xs" color="$colorMuted">Contact: {update.contactName}</AppText>
                              )}
                              {update.attachmentFileName && update.attachmentStoragePath && (
                                <AppButton className="mt-2" size="sm" tone="outline" onPress={() => void openAttachment(update)}>
                                  <ExternalLink size={12} />
                                  {update.attachmentFileName}
                                  {formatFileSize(update.attachmentFileSize) ? ` · ${formatFileSize(update.attachmentFileSize)}` : ''}
                                </AppButton>
                              )}
                            </AppCard>
                          ))}
                        </div>
                      </AppCard>
                    )}
                  </AppCard>
                );
              })}
            </div>
          )}
        </AppCard>
      </SectionAccordionCard>

      <SectionAccordionCard
        emoji="⚖️"
        title="Housing rights routes"
        subtitle="Keep the repair, tenancy, and moving routes together."
        open={openSection === 'routes'}
        onToggle={() => toggleSection('routes')}
      >
        <AppCard>
          <AppSectionHeader
            title="Housing rights journeys"
            subtitle="Keep rights-focused repair, tenancy, and moving actions inside the same shared home admin shell."
          />
          <div className="space-y-2">
            <QuickActionCard
              title="Repair issue"
              body="Log the issue, keep the evidence trail tidy, then use the home guides when you need wording or next-step confidence."
              onClick={() => navigate('/guides?section=home')}
            />
            <QuickActionCard
              title="Rent or contract change"
              body="Keep bills current so notices, rent changes, and regular household costs are not surprising you later."
              onClick={() => navigate('/home?tab=my-home&section=bills')}
            />
          </div>
        </AppCard>
      </SectionAccordionCard>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: number }) {
  return (
    <AppCard px="$sm" py="$sm">
      <AppText fontSize="$xs" fontWeight="700" textTransform="uppercase" letterSpacing={0.8} color="$colorMuted">
        {label}
      </AppText>
      <AppText className="mt-1" fontSize="$xl" fontWeight="700">
        {value}
      </AppText>
    </AppCard>
  );
}

function QuickActionCard({
  title,
  body,
  onClick,
}: {
  title: string;
  body: string;
  onClick: () => void;
}) {
  return (
    <AppPanelButton onClick={onClick} className="border-0 px-0 py-0">
      <AppCard backgroundColor="$background" px="$md" py="$sm">
        <div className="flex items-start gap-3">
          <FileText size={16} className="mt-0.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <AppText fontSize="$sm" fontWeight="700">{title}</AppText>
            <AppText className="mt-1" fontSize="$sm" color="$colorMuted">{body}</AppText>
          </div>
          <ArrowRight size={16} className="shrink-0 text-muted-foreground" />
        </div>
      </AppCard>
    </AppPanelButton>
  );
}
